import { Link } from 'react-router';
import { vnDate, vnShortDate } from '../../app/lib/coach';
import { Card, dateTime } from './blocks';

// The "Huấn luyện viên" tab of a coach's page (coach_tab of GET /api/admin/users/:id, UserInsight::CoachTab).

function Tile({ value, label, sub, tone }) {
  return (
    <div className="rounded-[16px] bg-base-100 p-3">
      <div className={`tabular text-2xl font-extrabold ${tone || ''}`}>{value}</div>
      <div className="text-xs font-semibold">{label}</div>
      {sub && <div className="text-[11px] text-muted">{sub}</div>}
    </div>
  );
}

export function CoachStats({ stats }) {
  const { students: s, offline, sessions, pending } = stats;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
      <Tile value={stats.seats} label="chỗ HLV" sub={stats.seats ? 'đang có' : 'chưa có chỗ'} />
      <Tile value={s.total} label="học viên dùng app" sub={`${s.free} Free · ${s.own_pro} Pro cá nhân · ${s.gifted} được tặng`} />
      <Tile value={`${offline.count}/${offline.limit}`} label="người chưa dùng app" sub="so với giới hạn" />
      <Tile value={sessions.total} label="buổi hẹn 7 ngày" sub={`${sessions.trained} đã tập · ${sessions.missed} vắng · ${sessions.off} báo nghỉ`} tone="text-[#17804f]" />
      <Tile value={pending.reschedules + pending.link_requests} label="yêu cầu chờ xử lý" sub={`${pending.reschedules} đổi giờ · ${pending.link_requests} kết nối`}
        tone={pending.reschedules + pending.link_requests ? 'text-[#c2410c]' : ''} />
    </div>
  );
}

export function SetupFunnel({ setup }) {
  const done = setup.filter((s) => s.at).length;
  const next = setup.find((s) => !s.at);
  return (
    <Card title="Thiết lập HLV" aside={`${done}/${setup.length} bước`}>
      <ol className="flex flex-col gap-1.5 text-sm">
        {setup.map((s) => (
          <li key={s.id} className={`flex items-center gap-2 ${s === next ? 'font-semibold' : ''}`}>
            <span className={`grid size-5 shrink-0 place-items-center rounded-full text-[11px] font-bold ${s.at ? 'bg-[#17804f] text-white' : s === next ? 'border-2 border-[#c2410c]' : 'border border-line'}`}>
              {s.at ? '✓' : ''}
            </span>
            <span className="min-w-0 flex-1">{s.label}</span>
            <span className="text-xs text-muted">{s.at ? vnShortDate(s.at) : '–'}</span>
          </li>
        ))}
      </ol>
      {next && <p className="text-xs text-muted">Bước tiếp theo: {next.label.toLowerCase()}</p>}
    </Card>
  );
}

const STATUS = { active: 'Đang dùng', pending: 'Chờ', refunded: 'Đã hoàn tiền', cancelled: 'Đã huỷ' };
const PERIOD = { monthly: 'tháng', quarterly: '3 tháng', half_yearly: '6 tháng', yearly: 'năm' };

export function SeatsBlock({ seats }) {
  return (
    <Card title="Chỗ HLV" aside="cấp tay đến khi bán qua App Store">
      {!seats ? <p className="text-sm text-muted">Chưa có chỗ nào.</p> : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
          <dt className="text-muted">Chỗ</dt><dd>{seats.seats} chỗ · dùng {seats.seats_used} · còn {seats.seats_free}</dd>
          <dt className="text-muted">Chu kỳ</dt><dd>{PERIOD[seats.period] || seats.period} · {seats.active ? `hết hạn ${vnDate(seats.period_ends_at)}` : 'đã hết hạn'}</dd>
          <dt className="text-muted">Trạng thái</dt><dd>{STATUS[seats.status] || seats.status}</dd>
          <dt className="text-muted">Đổi người tháng này</dt><dd>còn {seats.swaps_left} lượt</dd>
        </dl>
      )}
    </Card>
  );
}

const SOURCES = { invited: 'Link mời', self_joined: 'Mã HLV', offline: 'Chưa dùng app' };
const PLANS = { free: 'Miễn phí', own_pro: 'Pro cá nhân', gifted: 'HLV tặng Pro' };

export function StudentsTable({ students }) {
  return (
    <Card title="Học viên" aside={`${students.length} người`}>
      {students.length === 0 ? <p className="text-sm text-muted">Chưa có học viên.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase text-muted">
                <th className="py-1.5 font-semibold">Tên</th><th className="font-semibold">Nguồn</th><th className="font-semibold">Gói</th>
                <th className="font-semibold">7 ngày</th><th className="font-semibold">Buổi hẹn 30 ngày</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {students.map((s, i) => (
                <tr key={s.user_id || `offline-${i}`}>
                  <td className="py-2 font-semibold">{s.user_id ? <Link className="underline" to={`/admin/users/${s.user_id}`}>{s.name}</Link> : s.name}</td>
                  <td className="text-muted">{SOURCES[s.source] || s.source}</td>
                  <td>{PLANS[s.plan] || '–'}</td>
                  <td>{s.user_id ? `${s.days_opened}/7 ngày · ${s.meals} bữa` : '–'}</td>
                  <td>
                    {s.sessions.total ? `${s.sessions.trained}/${s.sessions.total} đã tập` : '–'}
                    {s.sessions.missed > 0 && <span className="ml-1.5 rounded-full bg-[#fdebe4] px-2 py-0.5 text-[11px] font-bold text-[#8e3414]">{s.sessions.missed} vắng</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-muted">Bấm tên để xem trang của học viên. Người chưa dùng app chỉ có dòng ở đây.</p>
    </Card>
  );
}

export function CoachFeatureUse({ use }) {
  const rows = [
    ['Xếp lịch hẹn', use.sessions_created, use.from_series ? `${use.from_series} lặp hằng tuần` : null],
    ['Duyệt đổi giờ', use.reschedules_decided, use.average_reply_hours != null ? `trung bình ${use.average_reply_hours} giờ` : null],
    ['Đặt mục tiêu cho học viên', use.targets_set, null],
    ['Đánh dấu Đã tập / Vắng', use.attendance_marked, null],
    ['Xuất ảnh lịch tuần', use.schedule_exports, null],
    ['Mời học viên', use.invites_sent, use.invites_sent ? `${use.invites_accepted} người nhận` : null],
  ];
  return (
    <Card title="HLV dùng tính năng gì" aside="14 ngày">
      <table className="w-full text-sm">
        <tbody className="divide-y divide-line">
          {rows.map(([label, n, note]) => (
            <tr key={label}>
              <td className="py-1.5">{label}</td>
              <td className="tabular py-1.5 text-right font-bold">{n}</td>
              <td className="py-1.5 pl-3 text-xs text-muted">{note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

const TAG_STYLES = { Coach: 'bg-[#e3f1e9] text-[#17804f]', 'Kết nối': 'bg-[#e8f0fe] text-[#1a56db]', Pro: 'bg-[#ece7fb] text-[#5b44b0]' };

export function CoachTimeline({ items }) {
  return (
    <Card title="Hoạt động huấn luyện gần đây" aside="mới nhất trước">
      {items.length === 0 ? <p className="text-sm text-muted">Chưa có hoạt động.</p> : (
        <ul className="flex flex-col divide-y divide-line">
          {items.map((it, i) => (
            <li key={`${it.at}-${i}`} className="flex items-start gap-3 py-2 text-sm">
              <span className="w-24 shrink-0 text-xs text-muted">{dateTime(it.at)}</span>
              <span className="min-w-0 flex-1">{it.text}</span>
              {it.tag && <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${TAG_STYLES[it.tag] || 'bg-base-200 text-muted'}`}>{it.tag}</span>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
